/**
 * Every string this surface renders.
 *
 * The copy addresses the operator, never the reviewer: no design rationale, no
 * technical vocabulary, and no restatement of a mechanic the screen already
 * performs. Where a figure is absent, the line says what the operator can do
 * about it — «where the reports are counted» — rather than why the surface does
 * not carry it.
 */
export const copy = {
  head: {
    title: 'لوحة التشغيل',
    lede: 'أرقام المستخدمين والمحتوى والتفاعل والإيراد كما هي عند فتح الصفحة. حدّث الصفحة لقراءة أحدث منها.',
  },

  sections: {
    users: { heading: 'المستخدمون', link: 'إدارة المستخدمين ←' },
    content: { heading: 'المحتوى', link: 'إدارة المحتوى ←' },
    engagement: { heading: 'التفاعل', link: 'طابور التعليقات ←' },
    revenue: { heading: 'الإيراد', link: 'تقارير الاشتراك ←' },
  },

  /** Table captions — visually hidden, and each names what its table partitions. */
  captions: {
    users: 'المستخدمون موزّعين بحسب مصدر الاستحقاق',
    content: 'العناوين موزّعة بحسب حالة النشر ونوعها',
    engagement: 'التفاعل المنشور موزّعاً بحسب نوعه',
    revenue: 'الاشتراكات الفاعلة والإيراد المحصّل بحسب الخطّة',
  },

  columns: {
    share: 'الحصّة',
    percent: 'النسبة',
    users: { row: 'مصدر الاستحقاق', value: 'المستخدمون' },
    content: { row: 'حالة النشر أو النوع', value: 'العناوين' },
    engagement: { row: 'نوع التفاعل', value: 'العدد' },
    revenue: { row: 'الخطّة', value: 'الإيراد المحصّل' },
  },

  total: 'المجموع',

  /**
   * Tile wording. The wire carries a key and an integer; the label is the
   * console's, because the service deliberately keeps shaping decisions on the
   * reading side of the hop. A key with no entry renders as itself rather than
   * being dropped — a figure the operator cannot name is still a figure, and a
   * missing one is a hole.
   */
  tiles: {
    account_count: 'إجمالي المستخدمين',
    blocked_account_count: 'حسابات محظورة',
    operator_count: 'المشغّلون',
    admin_count: 'المدراء',
    total_titles: 'إجمالي العناوين',
    credited_person_count: 'أشخاص منسوبون',
    category_count: 'التصنيفات',
    tag_count: 'الوسوم',
    editorial_read_count: 'قراءات المقالات',
    published_post_count: 'مقالات منشورة',
    reaction_positive_count: 'تفاعلات إيجابية',
    reaction_score_count: 'تقييمات',
    comment_count: 'تعليقات منشورة',
    follow_count: 'متابعات',
    comment_moderation_queue_depth: 'تعليقات بانتظار المراجعة',
    // The other two depths, which the owning service now states as tiles beside
    // the first. Unlabelled they fell through to their own keys and printed
    // `report_queue_depth` and `open_case_count` in Latin script on an
    // Arabic-only surface — a label falling through to its key is the one thing
    // this map exists to prevent.
    report_queue_depth: 'بلاغات مفتوحة',
    open_case_count: 'حالات مفتوحة',
    total_amount: 'الإيراد المحصّل',
    active_count: 'اشتراكات فاعلة',
    expired_count: 'اشتراكات منتهية',
    manual_grant_count: 'منح يدوية فاعلة',
  } as Readonly<Record<string, string>>,

  /** The line under a figure that waits on operator work. Colour is never the only signal. */
  pendingNotes: {
    comment_moderation_queue_depth: 'المراجعة قبل الظهور',
    report_queue_depth: 'بانتظار قرار',
    open_case_count: 'بانتظار ردّ',
  } as Readonly<Record<string, string>>,

  /** The three queue links the engagement section carries beneath its figures. */
  queueLinks: {
    comments: 'راجع التعليقات',
    cases: 'افتح الحالات',
  },

  notes: {
    content: 'الحالات غير المعروضة لا يبلغها عنوان في هذا الإصدار.',
    /**
     * Where the depths are is a fact about the page, so the sentence names it.
     *
     * The owning service moved all three depths out of the breakdown and into
     * the tiles, so they draw **above** this table among the section's own
     * figures — the separate depth table below it renders only if rows ever
     * come back. A note pointing under the table sent the reader past the only
     * place the depths appear.
     */
    engagement:
      'الجدول أعلاه يوزّع التفاعل المنشور بحسب نوعه. أعماق الطوابير بين الأرقام فوقه، وهي ليست نوعاً من التفاعل ولا تدخل في نسبه.',
    revenuePrefix: 'يُحصَّل الاشتراك عبر البوّابة الفاعلة. لتبديلها أو تعديل بياناتها:',
    revenueLink: 'إدارة بوّابات الدفع',
  },

  /**
   * The queue-depth block, drawn beneath a section's own breakdown.
   *
   * Separated because a depth is not a kind of the thing the table partitions:
   * comments awaiting review are a **subset** of the comments row, and open
   * reports and open cases are not interactions at all. Held inside the share
   * table they double-counted their own denominator and made every percentage
   * on the section wrong — and more wrong the more work was queued.
   *
   * No share column and no percentage: there is no whole for a depth to be a
   * part of. What it is, is a number of things waiting.
   */
  queueDepth: {
    heading: 'أعماق الطوابير',
    lede: 'ما ينتظر قراراً من مشغّل. ليست نوعاً من التفاعل، فلا نصيب لها ولا نسبة.',
    caption: 'أعماق طوابير العمل',
    rowColumn: 'الطابور',
    valueColumn: 'ما ينتظر',
  },

  states: {
    loading: 'جارٍ تحميل الأرقام',
    empty: 'لا أرقام في هذا القسم بعد.',
  },
} as const;
